<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue'
import { useFileDialog, useDebounceFn } from '@vueuse/core'
import { useVuelidate } from '@vuelidate/core'
import { email, required, helpers } from '@vuelidate/validators'
import { VModal, VButton, VIcon, VTextField, VTextArea } from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'
import { useTeamMemberStore } from '../stores/team-member-store'
import type { TeamMember } from '../types'

const props = defineProps<{
  member?: TeamMember | null
  disabled?: boolean
  candidateId: string
}>()

const teamMemberStore = useTeamMemberStore()

const emit = defineEmits<{
  (e: 'save', formData: FormData): void
  (e: 'cancel'): void
}>()

const modal = useModal()

const form = reactive({
  name: '',
  title: '',
  bio: '',
  email: '',
  linkedin_url: '',
})

const rules = {
  name: { required },
  email: { email },
  linkedin_url: {
    linkedinUrl: helpers.withMessage(
      'Must be a valid LinkedIn profile URL (e.g. https://linkedin.com/in/username)',
      (value: string) => !value || /^https?:\/\/(www\.)?linkedin\.com\/in\/.+$/i.test(value),
    ),
  },
}

const v$ = useVuelidate(rules, form, { $lazy: true, $scope: false })

const photoFile = ref<File | null>(null)
const photoPreview = ref<string | null>(null)
const linkedinPhotoUrl = ref<string | null>(null)
const linkedinLookupLoading = ref(false)
const linkedinImported = ref(false)
const showDetailsManually = ref(false)
const removeExistingPhoto = ref(false)

const isEditing = computed(() => !!props.member)

// Show details section if: editing, manually expanded, or LinkedIn imported data
const showDetails = computed(() => {
  return isEditing.value || showDetailsManually.value || linkedinImported.value
})

const currentPhotoUrl = computed(() => {
  if (photoPreview.value) return photoPreview.value
  if (!removeExistingPhoto.value && props.member?.photo_url) return props.member.photo_url
  return null
})

const resetForm = () => {
  if (props.member) {
    form.name = props.member.name || ''
    form.title = props.member.title || ''
    form.bio = props.member.bio || ''
    form.email = props.member.email || ''
    form.linkedin_url = props.member.linkedin_url || ''
  } else {
    form.name = ''
    form.title = ''
    form.bio = ''
    form.email = ''
    form.linkedin_url = ''
  }
  photoFile.value = null
  photoPreview.value = null
  linkedinPhotoUrl.value = null
  linkedinImported.value = false
  showDetailsManually.value = false
  removeExistingPhoto.value = false
}

const open = (memberToEdit?: TeamMember | null) => {
  v$.value.$reset()
  removeExistingPhoto.value = false
  if (memberToEdit) {
    form.name = memberToEdit.name || ''
    form.title = memberToEdit.title || ''
    form.bio = memberToEdit.bio || ''
    form.email = memberToEdit.email || ''
    form.linkedin_url = memberToEdit.linkedin_url || ''
    photoPreview.value = memberToEdit.photo_url || null
    linkedinImported.value = false
    showDetailsManually.value = false
  } else {
    resetForm()
  }
  modal.open()
}

const close = () => {
  modal.close()
  emit('cancel')
}

const expandManually = () => {
  showDetailsManually.value = true
}

const { files: photoFiles, open: openFileDialog } = useFileDialog({
  accept: 'image/*',
  multiple: false,
})

watch(photoFiles, (fileList) => {
  if (!fileList || fileList.length === 0) return
  const file = fileList[0]
  photoFile.value = file
  photoPreview.value = URL.createObjectURL(file)
  linkedinPhotoUrl.value = null
  removeExistingPhoto.value = false
})

const performLinkedinLookup = useDebounceFn(async (url: string) => {
  // Clear any previous LinkedIn URL validation error
  v$.value.linkedin_url.$reset()

  if (!/^(https?:\/\/)?(www\.)?linkedin\.com\/in\/.+$/i.test(url)) {
    // Let Vuelidate handle validation errors
    return
  }

  // Skip lookup if all auto-fill fields are already populated
  if (form.name.trim() && form.title.trim() && form.bio.trim()) return

  linkedinLookupLoading.value = true
  try {
    const result = await teamMemberStore.lookupLinkedin(props.candidateId, url)
    if (result) {
      form.name = result.name || form.name
      form.title = result.title || form.title
      form.bio = result.bio || form.bio
      if (result.photo_url && !photoFile.value) {
        linkedinPhotoUrl.value = result.photo_url
        photoPreview.value = result.photo_url
      }
      linkedinImported.value = true
    }
  } catch {
    // LinkedIn lookup failed - user can still fill manually
  } finally {
    linkedinLookupLoading.value = false
  }
}, 500)

watch(
  () => form.linkedin_url,
  (newUrl) => {
    if (newUrl) {
      performLinkedinLookup(newUrl)
    }
  },
)

const removePhoto = () => {
  photoFile.value = null
  photoPreview.value = null
  linkedinPhotoUrl.value = null
  removeExistingPhoto.value = true
}

const handleSubmit = async () => {
  const valid = await v$.value.$validate()
  if (!valid) return

  const formData = new FormData()
  formData.append('team_member[name]', form.name)
  formData.append('team_member[title]', form.title)
  formData.append('team_member[bio]', form.bio)
  formData.append('team_member[email]', form.email)
  formData.append('team_member[linkedin_url]', form.linkedin_url)

  if (photoFile.value) {
    formData.append('team_member[photo]', photoFile.value)
  } else if (linkedinPhotoUrl.value) {
    formData.append('team_member[photo_url]', linkedinPhotoUrl.value)
  } else if (removeExistingPhoto.value) {
    formData.append('team_member[remove_photo]', '1')
  }

  emit('save', formData)
}

const closeModal = () => {
  modal.close()
}

defineExpose({ open, close, closeModal })
</script>

<template>
  <VModal :modalStore="modal" :has_footer="true">
    <template #main>
      <div class="space-y-6">
        <h2 class="text-xl font-semibold">{{ isEditing ? 'Edit' : 'Add' }} Team Member</h2>

        <!-- LinkedIn URL Field - shown at top when collapsed -->
        <div v-if="!showDetails" class="space-y-2">
          <div class="relative">
            <VTextField
              v-model="form.linkedin_url"
              label="LinkedIn URL"
              property="linkedin_url"
              type="url"
              placeholder="https://linkedin.com/in/username"
              :disabled="disabled || linkedinLookupLoading"
              :v$="v$"
            />
            <div v-if="linkedinLookupLoading" class="absolute right-3 top-9 flex items-center">
              <svg
                class="h-4 w-4 animate-spin text-gray-400"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                <path
                  class="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
          </div>
          <p class="text-xs text-gray-500">
            Auto-fills name, title, bio, and photo — or
            <button type="button" @click="expandManually" class="text-blue-600 hover:text-blue-700 hover:underline">
              enter manually
            </button>
          </p>
        </div>

        <!-- Details Section - Expands after import or manual click -->
        <Transition
          enter-active-class="transition-all duration-300 ease-out"
          enter-from-class="opacity-0 max-h-0 overflow-hidden"
          enter-to-class="opacity-100 max-h-[1000px]"
          leave-active-class="transition-all duration-200 ease-in"
          leave-from-class="opacity-100 max-h-[1000px]"
          leave-to-class="opacity-0 max-h-0 overflow-hidden"
        >
          <div v-if="showDetails" class="space-y-6">
            <!-- Photo -->
            <div class="flex justify-center py-2">
              <div class="group relative cursor-pointer" @click="openFileDialog">
                <div
                  v-if="currentPhotoUrl"
                  class="relative h-24 w-24 overflow-hidden rounded-full ring-1 ring-inset ring-gray-200 transition-all group-hover:ring-gray-300"
                >
                  <img
                    :src="currentPhotoUrl"
                    class="absolute left-1/2 top-1/2 min-h-[140%] min-w-[140%] -translate-x-1/2 -translate-y-1/2 object-cover"
                    alt="Team member photo"
                  />
                </div>
                <div
                  v-else
                  class="flex h-24 w-24 items-center justify-center rounded-full bg-gray-50 ring-1 ring-inset ring-gray-200 transition-all group-hover:ring-gray-300"
                >
                  <VIcon name="Image" class="h-6 w-6 text-gray-400" />
                </div>

                <button
                  v-if="!currentPhotoUrl"
                  type="button"
                  @click.stop="openFileDialog"
                  class="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-gray-200 transition-transform hover:scale-110"
                  title="Add photo"
                >
                  <VIcon name="Plus" class="h-3 w-3 text-gray-600" />
                </button>

                <template v-if="currentPhotoUrl">
                  <button
                    type="button"
                    @click.stop="openFileDialog"
                    class="absolute bottom-0 left-0 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-gray-200 transition-transform hover:scale-110"
                    title="Change photo"
                  >
                    <VIcon name="Pencil" class="h-3 w-3 !fill-gray-800" />
                  </button>
                  <button
                    type="button"
                    @click.stop="removePhoto"
                    class="absolute bottom-0 right-0 flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-gray-200 transition-transform hover:scale-110"
                    title="Remove photo"
                  >
                    <VIcon name="Trash" class="h-3 w-3 text-red-500" />
                  </button>
                </template>
              </div>
            </div>

            <!-- LinkedIn URL - shown here when expanded -->
            <div class="relative">
              <VTextField
                v-model="form.linkedin_url"
                label="LinkedIn URL"
                property="linkedin_url"
                type="url"
                placeholder="https://linkedin.com/in/username"
                :disabled="disabled || linkedinLookupLoading"
                :v$="v$"
              />
              <div v-if="linkedinLookupLoading" class="absolute right-3 top-9 flex items-center">
                <svg
                  class="h-4 w-4 animate-spin text-gray-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <p v-if="!linkedinImported" class="mt-1 text-xs text-gray-500">Auto-fills name, title, bio, and photo</p>
            </div>

            <!-- Form Fields -->
            <VTextField
              v-model="form.name"
              label="Name"
              property="name"
              placeholder="Full name"
              :required="true"
              :disabled="disabled"
              :v$="v$"
            />

            <VTextField
              v-model="form.title"
              label="Title"
              property="title"
              placeholder="e.g. Managing Partner"
              :disabled="disabled"
            />

            <div>
              <VTextField
                v-model="form.email"
                label="Email"
                property="email"
                type="email"
                placeholder="email@example.com"
                :disabled="disabled"
                :v$="v$"
              />
              <p v-if="linkedinImported && !form.email" class="mt-1 text-xs text-amber-600">
                Email is not available from LinkedIn — please enter manually
              </p>
            </div>

            <VTextArea v-model="form.bio" label="Bio" property="bio" placeholder="Brief bio" :disabled="disabled" />
          </div>
        </Transition>
      </div>
    </template>

    <template #footer>
      <div class="flex justify-end gap-3">
        <VButton variant="secondary" :click="close">Cancel</VButton>
        <VButton variant="v-blue" :click="handleSubmit" :disabled="!showDetails">
          {{ isEditing ? 'Save Changes' : 'Add Member' }}
        </VButton>
      </div>
    </template>
  </VModal>
</template>
